import { logger as baseLogger } from "matrix-js-sdk/src/logger";
import { io, Socket } from "socket.io-client";
import { base64ToBytes, bytesToBase64 } from "../commons/cryptoUtils";
import { AESType, getAESInstance, IAES } from "../commons/AESManagerFactory";
import { PeerAESParametersV1, PeerPairingClientV1 } from "./PeerPairingClientV1";
import { CallKeyExchangeCompletedInfo, ICallKeyExchangeManager, RealtimeGatewayOptions } from "../commons/CallKeyExchangeManagerFactory";
import TimeoutError from "../../clientHttp/errors/TimeoutError";

const logger = baseLogger.getChild("CallKeyExchangeManagerV1");

/**
 * Gestisce la connessione al gateway realtime per lo scambio chiavi E2EE tra MASTER e PEER.
 * Utilizza socket.io per la comunicazione e PeerPairingClient per lo scambio chiavi ECDH.
 * Supporta i ruoli MASTER (fornisce la chiave LiveKit) e PEER (richiede la chiave LiveKit).
 */
export class CallKeyExchangeManagerV1 implements ICallKeyExchangeManager {
    private readonly AES_TYPE: AESType = AESType.AES_CBC;

    private socket!: Socket;
    private pairing!: PeerPairingClientV1;
    private connected: boolean = false;
    private authenticated: boolean = false;
    private handlersRegistered: boolean = false;
    private forceDisconnect: boolean = false;


    /**
     * Crea una nuova istanza di CallKeyExchangeManagerV1.
     * @param options Opzioni di configurazione per la connessione realtime.
     * @throws Se il ruolo è MASTER e manca la liveKitKey.
     */
    constructor(
        private readonly options: RealtimeGatewayOptions
    ) {
        // Genera una liveKitKey casuale se ruolo MASTER
        if (options.role === "MASTER" && !options.liveKitKey) {
            throw new Error("MASTER role requires liveKitKey");
        }

        // Rimuove slash finali dall'URL        
        options.url = options.url.replace(/\/+$/, "");
    }


    // #region Connection & Auth

    /**
     * Connette il client al gateway realtime con autenticazione integrata (auth param nella connessione).
     * Esegue direttamente join del canale e registra gli handler.
     * 
     * @param requestKeyExchange Se true e ruolo PEER, richiede lo scambio chiavi dopo il join del canale.
     * @throws Se già connesso.
     * @returns Promise risolta se connessione e join avvenuti con successo.
     */
    async connectAndJoinChannel(requestKeyExchange: boolean = true): Promise<boolean> {
        await this.internalConnect({ withAuth: true });

        // Esegue join del canale
        await this.joinChannel();

        // Registra gli handler per lo scambio chiavi
        this.registerKeyExchangeHandlers();

        // Se ruolo PEER, richiede lo scambio chiavi
        if (this.options.role === "PEER" && requestKeyExchange) {
            await this.requestKeyExchange();
        }

        return true;
    }

    /**
     * Connette il client al gateway realtime SENZA autenticazione automatica.
     * Dopo la connessione, è necessario chiamare loginAndJoinChannel() manualmente.
     */
    async connectOnly(): Promise<void> {
        await this.internalConnect({ withAuth: false });
        // Qui NON viene fatto login e joinChannel automaticamente
        // L'utente dovrà chiamare manualmente loginAndJoinChannel()
    }

    /**
     * Logica comune di connessione per connectAndJoinChannel e connectOnly.
     * @param options.withAuth Se true, connette con autenticazione.
     */
    private async internalConnect({ withAuth }: { withAuth: boolean }): Promise<void> {
        if (this.connected) {
            throw new Error("Already connected");
        }

        console.log("CallKeyExchangeManagerV1: Connecting to", this.options.url, "with auth:", withAuth);

        // Inizializza PeerPairingClient
        this.pairing = new PeerPairingClientV1();

        // Connette il socket con o senza auth
        this.socket = io(`${this.options.url}/rt`, {
            forceNew: true,
            reconnection: true,
            secure: true,
            transports: ["websocket"],
            reconnectionAttempts: 10,
            reconnectionDelay: 100,
            reconnectionDelayMax: 500,
            autoConnect: true,
            auth: withAuth ? { token: this.options.token } : undefined,
        });

        // Attende la connessione
        await this.waitForConnect();

        // Imposta stato
        this.connected = true;
        this.authenticated = withAuth;

        console.log("CallKeyExchangeManagerV1: Connected.");

        //#region Debug logs
        this.socket.on("connect", () => {
            logger.info("Socket connected: connection reestablished.");
        });
        this.socket.on("disconnect", (reason: string) => {
            logger.warn("Socket disconnected:", reason);
            if (reason === "io server disconnect") {
                // Disconnetto prima di riconnettere
                this.disconnect();

                // Se non forzato a disconnettersi, tenta di riconnettersi
                if (!this.forceDisconnect) {
                    // Forza la riconnessione automatica
                    logger.info("Forzo riconnessione dopo 'io server disconnect'");
                    // Riconnetto e rifaccio join canale
                    this.connectAndJoinChannel();
                }
            }
        });
        this.socket.on("connect_error", (err: any) => {
            logger.error("Socket connect_error:", err);
        });
        // this.socket.on("reconnect_attempt", (attempt: number) => {
        //     console.info("[CallKeyExchangeManagerV1] Socket reconnect_attempt:", attempt);
        // });
        // this.socket.on("reconnect", (attempt: number) => {
        //     console.info("[CallKeyExchangeManagerV1] Socket reconnect success:", attempt);
        // });
        // this.socket.on("ping", () => {
        //     console.debug("[CallKeyExchangeManagerV1] socket.io native ping sent");
        // });
        // this.socket.on("pong", (latency: number) => {
        //     console.debug("[CallKeyExchangeManagerV1] socket.io native pong received. Latency:", latency);
        // });
        // // Log ogni evento generico ricevuto (debug avanzato)
        // this.socket.onAny((event, ...args) => {
        //     if (event !== "ping" && event !== "pong") {
        //         console.debug(`[CallKeyExchangeManagerV1] socket.io event: ${event}`, ...args);
        //     }
        // });

        //#endregion
    }

    /**
     * Esegue login e join del canale dopo una connessione senza auth.
     * @param requestKeyExchange Se true e ruolo PEER, richiede lo scambio chiavi dopo il join del canale.
     * @throws Se non connesso o già autenticato.
     * @returns Promise risolta se login e join avvenuti con successo.
     */
    async loginAndJoinChannel(requestKeyExchange: boolean = true): Promise<void> {
        if (!this.connected) {
            throw new Error("Not connected");
        }
        if (this.authenticated) {
            throw new Error("Already authenticated");
        }

        // Esegue login
        await this.login();
        this.authenticated = true;

        // Esegue join del canale
        await this.joinChannel();

        // Registra gli handler per lo scambio chiavi
        this.registerKeyExchangeHandlers();

        // Se ruolo PEER, richiede lo scambio chiavi
        if (this.options.role === "PEER" && requestKeyExchange) {
            await this.requestKeyExchange();
        }
    }

    /**
     * Esegue il login esplicito tramite socket (da usare dopo connectWithoutAuth).
     * @returns Promise risolta se login avvenuto con successo.
     */
    private async login(): Promise<void> {
        if (!this.connected) {
            throw new Error("Cannot login: not connected");
        }
        return new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "auth:login",
                { token: this.options.token },
                (ack: { ok: boolean }) => {
                    ack?.ok ? resolve() : reject(new Error("Login failed"));
                }
            );
        });
    }

    /**
     * Attende la connessione del socket.
     * @returns Promise risolta alla connessione.
     */
    private waitForConnect(): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            this.socket.once("connect", resolve);
            this.socket.once("connect_error", reject);
        });
    }

    // #endregion


    // #region Channel

    /**
     * Effettua il join del canale specificato nelle opzioni.
     * @returns Promise risolta se join avvenuto con successo.
     */
    private async joinChannel(): Promise<void> {
        if (!this.connected) {
            throw new Error("Cannot join channel: not connected");
        }
        if (!this.authenticated) {
            throw new Error("Cannot join channel: not authenticated");
        }
        return new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "channel:join",
                {
                    channel: this.options.channel,
                    role: this.options.role,
                },
                (ack: { ok: boolean }) => {
                    ack?.ok ? resolve() : reject(new Error("Channel join failed"));
                }
            );
        });
    }

    /**
     * Lascia il canale corrente.
     */
    leaveChannel(): void {
        if (!this.connected) {
            throw new Error("Cannot leave channel: not connected");
        }
        if (!this.authenticated) {
            throw new Error("Cannot leave channel: not authenticated");
        }
        this.socket.emit("channel:leave", {
            channel: this.options.channel,
        });
    }

    // #endregion


    // #region Key exchange

    /**
     * Richiede lo scambio chiavi (solo PEER).
     * @param timeoutMs Timeout massimo di attesa risposta dal MASTER. 
     * se <=0, attende indefinitamente.
     * @throws Se il ruolo non è PEER.
     * @returns Promise risolta se la richiesta va a buon fine.
     */
    async requestKeyExchange(timeoutMs: number = 0): Promise<void> {
        if (this.options.role !== "PEER") {
            throw new Error("Only PEER can request key exchange");
        }

        const publicKey: string = bytesToBase64(this.pairing.getCompressedPublicKey());

        const emitPromise: Promise<void> = new Promise<void>((resolve, reject) => {
            this.socket.emit(
                "key_exchange:request",
                {
                    channel: this.options.channel,
                    publicKey,
                },
                (ack: { ok: boolean }) => {
                    ack?.ok
                        ? resolve()
                        : reject(new Error("Key exchange request rejected"));
                }
            );
        });

        // Gestisce il timeout se specificato
        if (timeoutMs > 0) {
            const timeoutPromise: Promise<void> = new Promise<void>((_, reject) =>
                setTimeout(
                    () => reject(new TimeoutError("Key exchange timeout")),
                    timeoutMs
                )
            );

            return Promise.race([emitPromise, timeoutPromise]);
        }

        // Altrimenti torna direttamente la promise dell'emit 
        return emitPromise;
    }

    /**
     * Registra gli handler per lo scambio chiavi in base al ruolo.
     * Evita registrazioni duplicate.
     */
    private registerKeyExchangeHandlers(): void {
        if (this.handlersRegistered) return;
        this.handlersRegistered = true;

        if (this.options.role === "MASTER") {
            this.registerMasterHandlers();
        } else {
            this.registerPeerHandlers();
        }
    }

    /**
     * Valida e decodifica una chiave pubblica ECDH in formato Base64.
     * Curve: P-521 → chiave compressa = 67 byte.
     * @param base64 Chiave pubblica Base64.
     */
    private validatePublicKey(base64: string): Uint8Array {
        const buf = base64ToBytes(base64);

        if (buf.length !== 67) {
            throw new Error("Invalid ECDH public key length");
        }

        return buf;
    }

    // #region MASTER

    /**
     * Registra gli handler per la gestione delle richieste di scambio chiavi lato MASTER.
     */
    private registerMasterHandlers(): void {
        this.socket.on(
            "key_exchange:request",
            async ({ fromSocketId, publicKey }: { fromSocketId: string; publicKey: string }) => {
                logger.info("key_exchange:request event received. Socket connected:", this.socket.connected);
                const peerPublicKey = this.validatePublicKey(publicKey);

                console.log("Peer Public Key received:", bytesToBase64(peerPublicKey));

                const { key, iv }: PeerAESParametersV1 =
                    await this.pairing.deriveAESParameters(peerPublicKey);

                const aes: IAES = await getAESInstance(this.AES_TYPE, key, iv);

                const encryptedLiveKitKey: string = bytesToBase64(
                    await aes.encrypt(this.options.liveKitKey!)
                );

                const masterPublicKeyBuf = bytesToBase64(this.pairing.getCompressedPublicKey());

                console.log("LiveKit E2EE Key Encrypted generated:", encryptedLiveKitKey);
                console.log("LiveKit E2EE Public Key generated:", masterPublicKeyBuf);

                // Chiama l'hook con la chiave LiveKit
                // Il Master può usare direttamente la chiave
                this.onKeyExchangeCompleted?.({ liveKitKey: this.options.liveKitKey! });

                this.socket.emit(
                    "key_exchange:response",
                    {
                        channel: this.options.channel,
                        toSocketId: fromSocketId,
                        encryptedLiveKitKey,
                        masterPublicKey: masterPublicKeyBuf,
                    },
                    (ack: { ok: boolean }) => {
                        if (!ack?.ok) {
                            console.error("Key exchange response failed");
                        }
                    }
                );

                // Forza la disconnessione dopo aver inviato la chiave
                // 3 secondi di ritardo per garantire l'invio e la ricezione della risposta
                setTimeout(() => {
                    logger.info("Disconnessione dopo invio chiave E2EE LiveKit al PEER.");
                    this.forceDisconnect = true;
                }, 3000);
            }
        );
    }

    // #endregion


    // #region PEER

    /**
     * Registra gli handler per la gestione della risposta di scambio chiavi lato PEER.
     */
    private registerPeerHandlers(): void {
        this.socket.on(
            "key_exchange:response",
            async ({ encryptedLiveKitKey, masterPublicKey }: { encryptedLiveKitKey: string; masterPublicKey: string }) => {
                const masterPublicKeyBuf: Uint8Array =
                    this.validatePublicKey(masterPublicKey);

                const { key, iv }: PeerAESParametersV1 =
                    await this.pairing.deriveAESParameters(masterPublicKeyBuf);

                const aes: IAES = await getAESInstance(this.AES_TYPE, key, iv);

                const liveKitKey: Uint8Array = await aes.decrypt(base64ToBytes(encryptedLiveKitKey));

                this.onKeyExchangeCompleted?.({ liveKitKey });
            }
        );
    }

    // #endregion
    // #endregion

    // #region Hooks

    /**
     * Hook chiamato lato PEER quando la chiave LiveKit è pronta.
     */
    onKeyExchangeCompleted?: (info: CallKeyExchangeCompletedInfo) => void;

    // #endregion


    // #region Cleanup

    /**
     * Disconnette il client dal gateway realtime e libera le risorse.
     */
    disconnect(): void {
        // console.log("CallKeyExchangeManagerV1: disconnecting...");
        if (!this.connected) return;
        console.log("CallKeyExchangeManagerV1: disconnected.");

        this.socket.off("key_exchange:request");
        this.socket.off("key_exchange:response");

        try {
            if (this.connected && this.authenticated)
                this.leaveChannel();

            if (this.connected)
                this.socket.disconnect();
        } catch (e) {
            console.error("Error during CallKeyExchangeManagerV1 disconnect:", e);
        }

        this.connected = false;
        this.handlersRegistered = false;
    }

    // #endregion
}
